import { ReactNode } from 'react'
import { Alert, Box, Divider, Grid2, Typography, useTheme } from '@mui/material'
import { AiOutlineDropbox } from 'react-icons/ai'
import { BsCurrencyDollar } from 'react-icons/bs'
import { FaBox, FaInbox, FaUsers } from 'react-icons/fa'
import { FiShoppingCart } from 'react-icons/fi'
import { IoNewspaperOutline, IoPricetag } from 'react-icons/io5'
import { MdOutlinePrecisionManufacturing } from 'react-icons/md'
import { BaseScreen } from '../../Components/Templates/BaseScreen/basescreen.component'
import { IndicatorCard, ShortcutCard } from './dashboard-cards.component'
import { DashboardChartPanel } from './dashboard-charts.component'
import { DashboardWelcome } from './dashboard-welcome.component'
import { useHubDashboard } from './dashboard.hook'

const iconSize = 18

const Dashboard = () => {
  const theme = useTheme()
  const {
    stats,
    ordersChart,
    chartsLoading,
    showOrdersChart,
    hasPermission,
    hasHcpAccess,
    user,
    loadError,
    failedModules,
    refreshDashboard,
  } = useHubDashboard()

  const dashboardColors = theme.palette.hubDashboard.modules

  const indicators = [
    hasPermission('menu:orders') && {
      key: 'orders',
      title: 'Pedidos',
      value: stats.orders ?? '—',
      icon: <IoNewspaperOutline size={iconSize} />,
      color: dashboardColors.orders,
    },
    hasPermission('menu:products') && {
      key: 'products',
      title: 'Produtos',
      value: stats.products ?? '—',
      icon: <FiShoppingCart size={iconSize} />,
      color: dashboardColors.products,
    },
    hasPermission('menu:customers') && {
      key: 'customers',
      title: 'Clientes',
      value: stats.customers ?? '—',
      icon: <FaUsers size={iconSize} />,
      color: dashboardColors.customers,
    },
    hasPermission('menu:collaborators') && {
      key: 'collaborators',
      title: 'Colaboradores',
      value: stats.collaborators ?? '—',
      icon: <FaUsers size={iconSize} />,
      color: dashboardColors.collaborators,
    },
    hasPermission('menu:stocks') && {
      key: 'stocks',
      title: 'Estoque',
      value: stats.stocks ?? '—',
      icon: <FaInbox size={iconSize} />,
      color: dashboardColors.stocks,
    },
    hasPermission('menu:production') && {
      key: 'production',
      title: 'Produção',
      value: stats.production ?? '—',
      icon: <MdOutlinePrecisionManufacturing size={iconSize} />,
      color: dashboardColors.production,
    },
  ].filter(Boolean) as Array<{
    key: string
    title: string
    value: string | number
    icon: ReactNode
    color: string
  }>

  const shortcuts = [
    hasPermission('menu:orders') && {
      title: 'Pedidos',
      subtitle: 'Vendas e status',
      icon: <IoNewspaperOutline size={iconSize} />,
      path: '/orders-list',
      color: dashboardColors.orders,
      badge: stats.orders ?? undefined,
    },
    hasPermission('menu:products') && {
      title: 'Produtos',
      subtitle: 'Catálogo',
      icon: <FiShoppingCart size={iconSize} />,
      path: '/products-list',
      color: dashboardColors.products,
      badge: stats.products ?? undefined,
    },
    hasPermission('menu:customers') && {
      title: 'Clientes',
      subtitle: 'Cadastro',
      icon: <FaUsers size={iconSize} />,
      path: '/customers-list',
      color: dashboardColors.customers,
      badge: stats.customers ?? undefined,
    },
    hasPermission('menu:collaborators') && {
      title: 'Colaboradores',
      subtitle: 'Permissões',
      icon: <FaUsers size={iconSize} />,
      path: '/collaborators-list',
      color: dashboardColors.collaborators,
      badge: stats.collaborators ?? undefined,
    },
    hasPermission('menu:sellers') && {
      title: 'Vendedores',
      subtitle: 'Equipe comercial',
      icon: <FaUsers size={iconSize} />,
      path: '/sellers-list',
      color: dashboardColors.sellers,
    },
    hasPermission('menu:stocks') && {
      title: 'Estoque',
      subtitle: 'Saldo',
      icon: <FaInbox size={iconSize} />,
      path: '/stocks-list',
      color: dashboardColors.stocks,
      badge: stats.stocks ?? undefined,
    },
    hasPermission('menu:production') && {
      title: 'Produção',
      subtitle: 'Fabricação',
      icon: <MdOutlinePrecisionManufacturing size={iconSize} />,
      path: '/production-list',
      color: dashboardColors.production,
      badge: stats.production ?? undefined,
    },
    hasPermission('menu:financial') && {
      title: 'Financeiro',
      subtitle: 'Faturamento',
      icon: <BsCurrencyDollar size={iconSize} />,
      path: '/financial-list',
      color: dashboardColors.financial,
    },
    hasPermission('menu:products') && {
      title: 'Política de Preços',
      subtitle: 'Tabelas',
      icon: <IoPricetag size={iconSize} />,
      path: '/price-policies-list',
      color: dashboardColors.pricePolicies,
    },
    hasPermission('menu:replacementOrders') && {
      title: 'Reposição',
      subtitle: 'Pedidos',
      icon: <IoNewspaperOutline size={iconSize} />,
      path: '/orders-replacement-list',
      color: dashboardColors.replacement,
    },
    hasPermission('menu:productsServices') && {
      title: 'Serviços',
      subtitle: 'Cadastro',
      icon: <IoNewspaperOutline size={iconSize} />,
      path: '/services-list',
      color: dashboardColors.services,
    },
    hasPermission('menu:boxes') && {
      title: 'Caixas',
      subtitle: 'Embalagens',
      icon: <AiOutlineDropbox size={iconSize} />,
      path: '/box-list',
      color: dashboardColors.boxes,
    },
    hasPermission('menu:cubages') && {
      title: 'Cubagem',
      subtitle: 'Volumes',
      icon: <FaBox size={iconSize} />,
      path: '/cubage-list',
      color: dashboardColors.cubages,
    },
    hasHcpAccess() && {
      title: 'HCP',
      subtitle: 'Controle da produção',
      icon: <MdOutlinePrecisionManufacturing size={iconSize} />,
      path: '/hcp/dashboard',
      color: theme.palette.primary.dark,
    },
  ].filter(Boolean) as Array<{
    title: string
    subtitle: string
    icon: ReactNode
    path: string
    color: string
    badge?: string | number
  }>

  const showCharts = showOrdersChart

  return (
    <BaseScreen variant="light">
      <Box sx={{ maxWidth: 1280, mx: 'auto', width: '100%' }}>
        <DashboardWelcome
          username={user?.username}
          onRefresh={refreshDashboard}
          refreshing={stats.loading}
        />

        {loadError && !stats.loading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {failedModules.length > 0
              ? `Falha ao carregar: ${failedModules.join(', ')}. Use o botão atualizar para tentar novamente.`
              : 'Não foi possível carregar todos os indicadores. Use o botão atualizar para tentar novamente.'}
          </Alert>
        )}

        {indicators.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1, px: 0.5 }}>
              Indicadores
            </Typography>
            <Grid2 container spacing={1}>
              {indicators.map((item) => (
                <Grid2 key={item.key} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                  <IndicatorCard
                    title={item.title}
                    value={item.value}
                    icon={item.icon}
                    color={item.color}
                    loading={stats.loading}
                  />
                </Grid2>
              ))}
            </Grid2>
          </Box>
        )}

        {showCharts && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1, px: 0.5 }}>
              Gráficos
            </Typography>
            <Grid2 container spacing={1.5}>
              {showOrdersChart && (
                <Grid2 size={{ xs: 12 }}>
                  <DashboardChartPanel
                    title="Pedidos por status"
                    subtitle="Distribuição atual na base"
                    data={ordersChart}
                    loading={chartsLoading}
                    type="bar"
                    emptyMessage="Nenhum pedido encontrado nos status monitorados."
                  />
                </Grid2>
              )}
            </Grid2>
          </Box>
        )}

        {shortcuts.length > 0 && (
          <Box>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1, px: 0.5 }}>
              Acesso rápido
            </Typography>
            <Grid2 container spacing={1}>
              {shortcuts.map((item) => (
                <Grid2 key={item.path} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <ShortcutCard
                    title={item.title}
                    subtitle={item.subtitle}
                    icon={item.icon}
                    path={item.path}
                    color={item.color}
                    badge={!stats.loading ? item.badge : undefined}
                  />
                </Grid2>
              ))}
            </Grid2>
          </Box>
        )}

        {!stats.loading && indicators.length === 0 && shortcuts.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum módulo disponível para o seu perfil.
            </Typography>
          </Box>
        )}
      </Box>
    </BaseScreen>
  )
}

export { Dashboard }
